import { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";

export interface PlanNode {
  id: string;
  parent_id: string | null;
  name: string;
  special: boolean;
  is_new_product: boolean;
}

interface Props {
  nodes: PlanNode[];
  selectedLeafId: string | null;
  onSelectLeaf: (nodeId: string) => void;
}

// Left pane of the add-product picker: drill down the TopDown plan's node
// tree (read-only mirror of the plan). Nodes with children drill in; leaves
// select and load their products in the right pane. The breadcrumb pops back
// up to any ancestor level.
export default function NodeDrilldown({ nodes, selectedLeafId, onSelectLeaf }: Props) {
  // Breadcrumb path of drilled-into node ids; [] = root level.
  const [path, setPath] = useState<string[]>([]);

  const { childrenByParent, nodesById } = useMemo(() => {
    const byParent = new Map<string | null, PlanNode[]>();
    const byId = new Map<string, PlanNode>();
    for (const node of nodes) {
      byId.set(node.id, node);
      const list = byParent.get(node.parent_id) ?? [];
      list.push(node);
      byParent.set(node.parent_id, list);
    }
    return { childrenByParent: byParent, nodesById: byId };
  }, [nodes]);

  const currentParentId = path.length > 0 ? path[path.length - 1] : null;
  const currentLevel = childrenByParent.get(currentParentId) ?? [];

  const handleNodeClick = (node: PlanNode) => {
    if (childrenByParent.has(node.id)) {
      setPath((prev) => [...prev, node.id]);
    } else {
      onSelectLeaf(node.id);
    }
  };

  return (
    <div className="flex min-h-0 flex-col gap-2">
      {/* Fixed h-6 header: keeps the list box's top edge level with the
          right pane's, which reserves the same header height. */}
      <div className="flex h-6 shrink-0 items-center gap-1 overflow-x-auto whitespace-nowrap text-xs text-muted-foreground">
        <button
          type="button"
          onClick={() => setPath([])}
          className={`shrink-0 ${path.length === 0 ? "font-medium text-foreground" : "hover:text-foreground"}`}
        >
          All
        </button>
        {path.map((nodeId, i) => (
          <span key={nodeId} className="flex shrink-0 items-center gap-1">
            <ChevronRight size={12} />
            <button
              type="button"
              onClick={() => setPath((prev) => prev.slice(0, i + 1))}
              className={i === path.length - 1 ? "font-medium text-foreground" : "hover:text-foreground"}
            >
              {nodesById.get(nodeId)?.name}
            </button>
          </span>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
        {currentLevel.map((node) => {
          const hasChildren = childrenByParent.has(node.id);
          const selected = node.id === selectedLeafId;
          return (
            <button
              key={node.id}
              type="button"
              onClick={() => handleNodeClick(node)}
              className={`flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50 ${
                selected ? "bg-muted font-medium" : ""
              }`}
            >
              <span className="truncate text-foreground">{node.name}</span>
              {hasChildren && <ChevronRight size={14} className="shrink-0 text-muted-foreground" />}
            </button>
          );
        })}
        {currentLevel.length === 0 && (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">No nodes.</div>
        )}
      </div>
    </div>
  );
}
